import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useStore } from 'react-redux';
import { firstValueFrom, Observable } from 'rxjs';

import { useOwnerParams } from 'hooks/use-owner-params.hook';
import { apiClient } from 'store/api-client';
import { toggleResourceFavorite } from 'store/resource-favorite/actions';
import { RootState } from 'store/reducer';
import { transformErrorIntoSerializableError } from 'store/utils';
import { FAVORITE_RESOURCE_UUIDS_QUERY_KEY } from 'data/queries/useFavoriteResourceUuidsQuery.hook';

interface ToggleResourceFavoriteVariables {
  resourceUuid: string;
  nextIsFavorite: boolean;
}

/**
 * Favorites or un-favorites a resource from the native map's details card — the same two calls the
 * list's star makes, run through TanStack Query so the map's favorites query can be refetched.
 * The confirmed result is also dispatched as the redux action the list's reducers already handle, so
 * going back to the list does not show the old star. A failure is raised as the list's failure action.
 */
export const useToggleResourceFavoriteMutation = () => {
  const store = useStore<RootState>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { ownerType } = useOwnerParams();

  return useMutation({
    mutationFn: async ({ resourceUuid, nextIsFavorite }: ToggleResourceFavoriteVariables): Promise<void> => {
      const client = apiClient(store.getState());
      const params = { ownerType, resourceUuid };

      const request: Observable<unknown> = nextIsFavorite
        ? client.addResourceFavorite(params)
        : client.removeResourceFavorite(params);

      await firstValueFrom(request);
    },
    // Returned so the mutation stays pending until the refetch lands — no flicker back to the old star.
    onSuccess: (_result, { resourceUuid, nextIsFavorite }) => {
      dispatch(toggleResourceFavorite.success({ resourceUuid, isFavorite: nextIsFavorite }));

      return queryClient.invalidateQueries({ queryKey: [FAVORITE_RESOURCE_UUIDS_QUERY_KEY] });
    },
    // The same failure action the list's star raises, so the error notification and the list's
    // reducers behave identically wherever the star is tapped.
    onError: (error: Error, { resourceUuid, nextIsFavorite }) => {
      dispatch(
        toggleResourceFavorite.failure({
          error: transformErrorIntoSerializableError(error),
          resourceUuid,
          isFavorite: !nextIsFavorite,
        }),
      );
    },
  });
};
